<template>
  <section class="ak-credentials" aria-label="Integration credentials">
    <div class="ak-credentials__intro">
      <div class="ak-credentials__intro-copy">
        <div class="ak-credentials__title-row">
          <h2 class="ak-credentials__title">Integration credentials</h2>
          <v-btn
            variant="outlined"
            size="small"
            class="pl-toolbar-btn pl-toolbar-btn--ghost ak-credentials__refresh"
            prepend-icon="mdi-refresh"
            :loading="refreshing"
            :disabled="disabled || refreshing"
            @click="$emit('refresh')"
          >
            Refresh
          </v-btn>
        </div>
        <p class="ak-credentials__subtitle">
          Authenticate programmatic access with an API key or OAuth 2.0 client credentials.
        </p>
      </div>
    </div>

    <div class="ak-credentials__grid">
      <!-- API Key card -->
      <article class="ak-cred-card ak-cred-card--api">
        <div class="ak-cred-card__glow" aria-hidden="true" />
        <div class="ak-cred-card__header">
          <div class="ak-cred-card__icon-wrap ak-cred-card__icon-wrap--api">
            <v-icon icon="mdi-key-variant" size="22" />
          </div>
          <div class="ak-cred-card__heading">
            <h3 class="ak-cred-card__name">API key</h3>
            <p class="ak-cred-card__desc">
              Send as the <code>x-api-key</code> header on every request, alongside
              the OAuth token below. Both are required.
            </p>
          </div>
          <span
            class="ak-cred-card__badge"
            :class="hasApiKey ? 'ak-cred-card__badge--active' : 'ak-cred-card__badge--idle'"
          >
            {{ hasApiKey ? 'Active' : 'Not configured' }}
          </span>
        </div>

        <div class="ak-cred-card__body">
          <CredentialSecretField
            v-if="hasApiKey"
            label="Secret key"
            :value="apiKey"
            hint="Required on every request as the x-api-key header. Yours alone — regenerating it stops any integration still using the old value."
            @copied="$emit('copied', 'API key copied')"
            @copy-error="$emit('copy-error')"
          />

          <div v-else class="ak-cred-card__empty">
            <v-icon icon="mdi-key-outline" size="28" />
            <p>API key unavailable — refresh, or contact support.</p>
          </div>
        </div>

        <div class="ak-cred-card__footer">
          <v-btn
            size="large"
            variant="flat"
            class="ak-cred-card__cta"
            :prepend-icon="hasApiKey ? 'mdi-refresh' : 'mdi-key-plus'"
            :loading="generatingApiKey"
            :disabled="disabled || generatingApiKey"
            @click="openConfirm('apiKey')"
          >
            {{ hasApiKey ? 'Regenerate key' : 'Generate API key' }}
          </v-btn>
        </div>
      </article>

      <!-- OAuth Client card -->
      <article class="ak-cred-card ak-cred-card--oauth">
        <div class="ak-cred-card__glow" aria-hidden="true" />
        <div class="ak-cred-card__header">
          <div class="ak-cred-card__icon-wrap ak-cred-card__icon-wrap--oauth">
            <v-icon icon="mdi-shield-key-outline" size="22" />
          </div>
          <div class="ak-cred-card__heading">
            <h3 class="ak-cred-card__name">OAuth client</h3>
            <p class="ak-cred-card__desc">
              Client credentials flow for server-to-server integrations.
            </p>
          </div>
          <span
            class="ak-cred-card__badge"
            :class="hasOAuthClient ? 'ak-cred-card__badge--active' : 'ak-cred-card__badge--idle'"
          >
            {{ hasOAuthClient ? 'Active' : 'Not configured' }}
          </span>
        </div>

        <div class="ak-cred-card__body">
          <template v-if="hasOAuthClient">
            <CredentialSecretField
              label="Client ID"
              :value="oauthClientId"
              :secret="false"
              @copied="$emit('copied', 'Client ID copied')"
              @copy-error="$emit('copy-error')"
            />
            <CredentialSecretField
              label="Client secret"
              :value="oauthClientSecret"
              hint="Masked for security — use copy to grab the full value."
              @copied="$emit('copied', 'Client secret copied')"
              @copy-error="$emit('copy-error')"
            />
            <CredentialSecretField
              v-if="oauthTokenUrl"
              label="Token URL"
              :value="oauthTokenUrl"
              :secret="false"
              @copied="$emit('copied', 'Token URL copied')"
              @copy-error="$emit('copy-error')"
            />
          </template>

          <div v-else class="ak-cred-card__empty">
            <v-icon icon="mdi-shield-outline" size="28" />
            <p>No OAuth client yet. Generate credentials for token-based access.</p>
          </div>
        </div>

        <div class="ak-cred-card__footer">
          <v-btn
            size="large"
            variant="flat"
            class="ak-cred-card__cta ak-cred-card__cta--oauth"
            :prepend-icon="hasOAuthClient ? 'mdi-refresh' : 'mdi-shield-plus-outline'"
            :loading="generatingOAuth"
            :disabled="disabled || generatingOAuth"
            @click="openConfirm('oauth')"
          >
            {{ hasOAuthClient ? 'Regenerate client' : 'Generate OAuth client' }}
          </v-btn>
        </div>
      </article>
    </div>

    <v-dialog v-model="confirmDialog" max-width="460" persistent>
      <v-card rounded="xl" class="user-dialog-card">
        <div class="dialog-header d-flex align-center justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="dialog-header__icon">
              <v-icon color="warning" size="22">mdi-alert-outline</v-icon>
            </div>
            <span class="dialog-header__title">{{ confirmTitle }}</span>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="dialog-close-btn"
            rounded="lg"
            :disabled="confirmLoading"
            @click="confirmDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="dialog-body dialog-body--message">
          {{ confirmMessage }}
        </v-card-text>
        <v-card-actions class="dialog-footer">
          <v-spacer />
          <v-btn
            variant="text"
            class="text-none dialog-btn dialog-btn--muted"
            rounded="lg"
            :disabled="confirmLoading"
            @click="confirmDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="text-none dialog-btn"
            rounded="lg"
            :loading="confirmLoading"
            @click="confirmAction"
          >
            {{ confirmActionLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import CredentialSecretField from '@/components/ApiKeys/CredentialSecretField.vue'

defineOptions({ name: 'OrgCredentialsPanel' })

const props = defineProps({
  apiKey: { type: String, default: '' },
  oauthClientId: { type: String, default: '' },
  oauthClientSecret: { type: String, default: '' },
  oauthTokenUrl: { type: String, default: '' },
  generatingApiKey: { type: Boolean, default: false },
  generatingOAuth: { type: Boolean, default: false },
  refreshing: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits([
  'generate-api-key',
  'generate-oauth',
  'copied',
  'copy-error',
  'refresh',
])

const confirmDialog = ref(false)
const confirmType = ref('apiKey')

const hasApiKey = computed(() => Boolean(String(props.apiKey ?? '').trim()))
const hasOAuthClient = computed(() =>
  Boolean(String(props.oauthClientId ?? '').trim()),
)

const confirmLoading = computed(() =>
  confirmType.value === 'apiKey'
    ? props.generatingApiKey
    : props.generatingOAuth,
)

const confirmTitle = computed(() =>
  confirmType.value === 'apiKey'
    ? hasApiKey.value
      ? 'Regenerate API key'
      : 'Generate API key'
    : hasOAuthClient.value
      ? 'Regenerate OAuth client'
      : 'Generate OAuth client',
)

const confirmMessage = computed(() => {
  if (confirmType.value === 'apiKey') {
    return hasApiKey.value
      ? 'This will invalidate your current API key immediately. Update any integrations using the old key.'
      : 'Create a new API key for your organisation? You can copy it after generation.'
  }

  return hasOAuthClient.value
    ? 'This will invalidate your current OAuth client credentials. Update any integrations using the old client.'
    : 'Create OAuth client credentials for server-to-server authentication?'
})

const confirmActionLabel = computed(() =>
  confirmType.value === 'apiKey'
    ? hasApiKey.value
      ? 'Regenerate'
      : 'Generate'
    : hasOAuthClient.value
      ? 'Regenerate'
      : 'Generate',
)

function openConfirm(type) {
  confirmType.value = type
  confirmDialog.value = true
}

function confirmAction() {
  if (confirmType.value === 'apiKey') {
    emit('generate-api-key')
  } else {
    emit('generate-oauth')
  }
}

watch(
  () => [props.generatingApiKey, props.generatingOAuth],
  ([apiLoading, oauthLoading], [prevApi, prevOauth]) => {
    const wasLoading = prevApi || prevOauth
    const isLoading = apiLoading || oauthLoading
    if (wasLoading && !isLoading) {
      confirmDialog.value = false
    }
  },
)

defineExpose({
  closeConfirm() {
    if (!confirmLoading.value) {
      confirmDialog.value = false
    }
  },
})
</script>
